import axios from 'axios';
import qs from 'qs';
import { AddressComponents } from 'types/responses/AddressRecordSearch';
import ArcGISFindAddressCandidatesResponse from 'types/responses/ArcGISFindAddress';
import ArcGISGeocodeSuggestionResponse from 'types/responses/ArcGISSuggestion';
import ArcGISErrorResponse from 'types/responses/ArcGisError';
import { buildAddressStringFromRecord } from 'utils/helpers';

type GeocodedAddress = AddressComponents & {
  mailingAddress: string;
  county: string;
  score: number;
};

function formatArcGISGeocodeResponse(
  response: ArcGISFindAddressCandidatesResponse
): GeocodedAddress {
  if (!response.candidates) {
    return {
      score: 0,
      mailingAddress: '',
      address: '',
      addressUnit: '',
      city: '',
      county: '',
      state: '',
      zipCode: '',
    };
  }

  const rankedCandidates = response.candidates.sort((a, b) => {
    return b.score - a.score;
  });
  const { attributes } = rankedCandidates[0];

  const mailingAddress = buildAddressStringFromRecord({
    address: attributes.StAddr,
    addressUnit: `${attributes.UnitType} ${attributes?.UnitName}`,
    city: attributes.City,
    state: attributes.RegionAbbr,
    zipcode: attributes.Postal,
  });

  return {
    score: rankedCandidates[0].score,
    mailingAddress,
    address: attributes.StAddr,
    addressUnit: `${attributes.UnitType} ${attributes?.UnitName}`,
    city: attributes.City,
    state: attributes.RegionAbbr,
    county: attributes.Subregion,
    zipCode: attributes.Postal,
  };
}

function formatArcGISGeocodeSuggestResponse(response: ArcGISGeocodeSuggestionResponse): string[] {
  if (!response.suggestions) {
    return [];
  }
  return response.suggestions.map((suggestion) => suggestion.text);
}

export class GeocodeService {
  private apiToken: string;
  private forStorage: boolean;
  private countryCode: string;
  private categories: string[];

  constructor({
    apiToken,
    forStorage = false,
    countryCode = 'USA',
    categories = ['street address', 'subaddress', 'point address'],
  }: {
    apiToken: string;
    forStorage?: boolean;
    countryCode?: string;
    categories?: string[];
  }) {
    this.apiToken = apiToken;
    this.forStorage = forStorage;
    this.countryCode = countryCode;
    this.categories = categories;
  }

  async geocodeAddress(searchText: string, magicKey?: string): Promise<GeocodedAddress> {
    const query = qs.stringify({
      f: 'json',
      outFields: '*',
      SingleLine: searchText,
      token: this.apiToken,
      forStorage: this.forStorage,
      magicKey,
      countryCode: this.countryCode,
      category: this.categories.join(','),
    });

    const url = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?${query}`;
    const results = await axios.get<ArcGISFindAddressCandidatesResponse | ArcGISErrorResponse>(url);
    if ('error' in results.data) {
      throw new Error('Error geocoding address');
    }
    return formatArcGISGeocodeResponse(results.data);
  }

  async geocodeAddressSuggest(searchText: string): Promise<string[]> {
    const query = qs.stringify({
      f: 'json',
      text: searchText,
      token: this.apiToken,
      countryCode: this.countryCode,
      category: this.categories.join(','),
    });

    const url = `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest?${query}`;
    const results = await axios.get<ArcGISGeocodeSuggestionResponse | ArcGISErrorResponse>(url);
    if ('error' in results.data) {
      throw new Error(results.data.error.message);
    }
    return formatArcGISGeocodeSuggestResponse(results.data);
  }
}
